import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Instagram, Facebook, Linkedin } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import SEO from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

export default function Contact() {
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", privacy: false, company: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error("Bitte stimmen Sie der Datenschutzerklärung zu.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Ihre Nachricht wurde gesendet. Vielen Dank!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", privacy: false, company: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Senden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const socials = [
    { k: "instagram", icon: Instagram, url: settings.instagram },
    { k: "facebook", icon: Facebook, url: settings.facebook },
    { k: "linkedin", icon: Linkedin, url: settings.linkedin },
  ].filter((s) => s.url);

  return (
    <div className="on-dark violet-canvas fade-in flex-1 w-full">
      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-12 sm:py-20">
      <SEO title="Kontakt" description="Kontaktieren Sie Susanne Monika Gysin." />
      <div className="grid md:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#F4C542] mb-5">Kontakt</div>
          <h1 className="font-serif text-5xl sm:text-6xl font-light leading-tight">Schreiben Sie mir</h1>
          <p className="mt-6 text-lg text-[#D8B9EA] leading-relaxed">
            Ob Frage zu einem Werk, ein Gedanke oder ein Anliegen – ich freue mich über Ihre Nachricht.
          </p>
          <div className="mt-8 space-y-4 text-sm">
            {settings.contact_email && (
              <a href={`mailto:${settings.contact_email}`} className="flex items-center gap-3 text-[#D8B9EA] hover:text-[#F4C542]">
                <Mail className="w-5 h-5" strokeWidth={1.5} /> {settings.contact_email}
              </a>
            )}
            {settings.contact_phone && (
              <a href={`tel:${settings.contact_phone}`} className="flex items-center gap-3 text-[#D8B9EA] hover:text-[#F4C542]">
                <Phone className="w-5 h-5" strokeWidth={1.5} /> {settings.contact_phone}
              </a>
            )}
          </div>
          {socials.length > 0 && (
            <div className="flex gap-4 mt-8">
              {socials.map((s) => (
                <a key={s.k} href={s.url} target="_blank" rel="noreferrer" aria-label={s.k} className="text-[#B79FD1] hover:text-[#F4C542]">
                  <s.icon className="w-6 h-6" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
          <input type="text" name="company" value={form.company} onChange={upd("company")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <div>
            <Label htmlFor="c-name">Name *</Label>
            <Input id="c-name" data-testid="contact-name" required value={form.name} onChange={upd("name")} className="mt-1.5 bg-white rounded-md h-12" />
          </div>
          <div>
            <Label htmlFor="c-email">E-Mail *</Label>
            <Input id="c-email" data-testid="contact-email" type="email" required value={form.email} onChange={upd("email")} className="mt-1.5 bg-white rounded-md h-12" />
          </div>
          <div>
            <Label htmlFor="c-phone">Telefon (optional)</Label>
            <Input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={upd("phone")} className="mt-1.5 bg-white rounded-md h-12" />
          </div>
          <div>
            <Label htmlFor="c-subject">Betreff</Label>
            <Input id="c-subject" data-testid="contact-subject" value={form.subject} onChange={upd("subject")} className="mt-1.5 bg-white rounded-md h-12" />
          </div>
          <div>
            <Label htmlFor="c-message">Nachricht *</Label>
            <Textarea id="c-message" data-testid="contact-message" required rows={5} value={form.message} onChange={upd("message")} className="mt-1.5 bg-white rounded-md" />
          </div>
          <div className="flex items-start gap-3">
            <Checkbox id="c-privacy" data-testid="contact-privacy" checked={form.privacy} onCheckedChange={(v) => setForm({ ...form, privacy: !!v })} className="mt-0.5 border-[#B79FD1] data-[state=checked]:bg-[#F4C542] data-[state=checked]:text-[#32104F]" />
            <Label htmlFor="c-privacy" className="text-sm font-normal text-[#D8B9EA] leading-relaxed">
              Ich stimme der Verarbeitung meiner Daten gemäss Datenschutzerklärung zu.
            </Label>
          </div>
          <Button type="submit" data-testid="contact-submit" disabled={loading} className="w-full bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4] rounded-md h-12">
            {loading ? "Wird gesendet…" : "Nachricht senden"}
          </Button>
        </form>
      </div>
      </div>
    </div>
  );
}
